import axios from "axios";
import { appConfig } from "./AppConfig";
import { client } from "./api-client/sdk.gen";
import { AppRoutes } from "./utils/AppRoutes";
import { BrowserRouter } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { Footer } from "./components/base/Footer";
import { useEffect, useState, Suspense } from "react";
import { useUserInfoStore } from "./utils/store/userStore";
import { useCacheListStore } from "./utils/store/cacheList";
import { handleGenericCatchError } from "./utils/functions";
import { UserService, AdministrationService } from "./api-client";
import { useTranslatorStore } from "./utils/store/translatorStore";
import { useInputValuesStore } from "./utils/store/inputValuesStore";
import { useGlobalSettingsStore } from "./utils/store/globalSettings";
import type { CatchErrorType } from "./utils/types/shared/miscellaneous";
import { FooterStatusProvider } from "./components/base/FooterStatusContext";

client.setConfig({
  baseUrl: appConfig.ApiBaseUrl(),
});

export default function App() {
  const { setUserInfo } = useUserInfoStore();
  const { loadInputValues } = useInputValuesStore();
  const { setGlobalSettings } = useGlobalSettingsStore();
  const { changeLocale } = useTranslatorStore();
  const { loadCacheList } = useCacheListStore();

  const [loading, setLoading] = useState<boolean>(true);

  const handleAuthorization = (): void => {
    const queryParameters = new URLSearchParams(window.location.search);
    let token = queryParameters.get("token");

    if (token) {
      localStorage.setItem("tradepeg-iframe-token", token);
    } else {
      token = localStorage.getItem("tradepeg-iframe-token");
    }

    const baseToken = token || appConfig.getEnvToken();
    const AuthorizationToken = `Bearer ${baseToken}`;

    axios.defaults.headers.common["Authorization"] = AuthorizationToken;

    client.interceptors.request.use((request) => {
      request.headers.set("Authorization", AuthorizationToken);
      return request;
    });
  };

  const fetchData = async () => {
    try {
      const [userResponse, globalSettings] = await Promise.all([
        UserService.selfInfo(),
        AdministrationService.globalSettings(),
        loadInputValues(),
        loadCacheList(),
      ]);

      setUserInfo(userResponse.data?.data);
      changeLocale(userResponse.data?.data?.locale || "en-gb");
      setGlobalSettings(globalSettings.data?.data);
    } catch (err: CatchErrorType) {
      if (err.status === 401) {
        localStorage.removeItem("tradepeg-iframe-token");
        return;
      }
      handleGenericCatchError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleAuthorization();
    fetchData();
  }, []);

  if (loading) return null;

  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <FooterStatusProvider>
        <Suspense fallback={null}>
          <AppRoutes />
        </Suspense>
        <Footer />
        <Toaster />
      </FooterStatusProvider>
    </BrowserRouter>
  );
}
